/**
 * API раздела «Лиды» (воронки Avito и СберАвто).
 *
 * Таблицы: public.inspection_leads, public.inspection_lead_changes.
 * Edge function для обогащения: enrich-lead-autoteka (Bearer user JWT).
 */
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '@/config'
import { createClient } from '@/api/supabase'
import {
  type CallResult,
  type CancelReasonCode,
  type InspectionLead,
  type InspectionLeadChange,
  type InspectionLeadChangeRow,
  type InspectionLeadComment,
  type InspectionLeadRow,
  type InspectionLeadStage,
  type InspectionLeadStatus,
  type LeadEvent,
  type LeadPipeline,
  type LeadSms,
  type MangoCall,
  type MangoCallDetail,
  leadPipelineUi,
} from '@/types/inspection-lead'

export interface ListLeadsParams {
  /** Воронка. Лиды разных воронок на одной доске не смешиваются. */
  pipeline: LeadPipeline
  /** 'all' — без фильтра. */
  status: InspectionLeadStatus | 'all'
  /** Поиск по client_name / client_phone / vehicle_vin / item_id. */
  search?: string
  page?: number
  pageSize?: number
}

export interface ListLeadsResult {
  items: InspectionLeadRow[]
  total: number
}

const DEFAULT_PAGE_SIZE = 50

export async function listInspectionLeads(
  params: ListLeadsParams,
): Promise<ListLeadsResult> {
  const { pipeline, status, search, page = 1, pageSize = DEFAULT_PAGE_SIZE } = params
  const supabase = createClient()

  let q = supabase
    .from('inspection_leads')
    .select('*', { count: 'exact' })
    .eq('pipeline', pipeline)

  if (status !== 'all') q = q.eq('status', status)

  if (search && search.trim()) {
    const s = search.trim()
    // Числовой ввод — наш номер лида, номер объявления Avito или номер CRM
    // СберАвто, иначе ilike по нескольким полям.
    if (/^\d+$/.test(s)) {
      const no = s.length <= 9 ? `lead_no.eq.${s},` : ''
      q = q.or(`${no}item_id.eq.${s},partner_external_id.eq.${s}`)
    } else {
      const pat = `%${s}%`
      q = q.or(
        `client_name.ilike.${pat},client_phone.ilike.${pat},vehicle_vin.ilike.${pat},vehicle_license_plate.ilike.${pat}`,
      )
    }
  }

  q = q.order('created_at', { ascending: false })

  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  q = q.range(from, to)

  const { data, count, error } = await q
  if (error) throw new Error(error.message)

  const items: InspectionLeadRow[] = (data ?? []) as InspectionLead[]

  // Batch read-through: статус связанного заказа для тех лидов, что
  // сконвертированы в заказ (order_id IS NOT NULL). Один SELECT на всю
  // страницу — дешевле, чем N запросов с фронта. Поле `order_status`
  // используется в таблице InspectionLeadsPage как отдельная колонка
  // «Заказ» рядом со статусом лида; время осмотра — метка канбана
  // «нет времени осмотра».
  const orderIds = items.map((r) => r.order_id).filter((v): v is string => !!v)
  if (orderIds.length > 0) {
    const { data: orders } = await supabase
      .from('orders')
      .select('id, status, inspection_scheduled_at')
      .in('id', orderIds)
    type OrderRef = { id: string; status: string; inspection_scheduled_at: string | null }
    const byId = new Map<string, OrderRef>(((orders ?? []) as OrderRef[]).map((o) => [o.id, o]))
    for (const r of items) {
      if (!r.order_id) continue
      const o = byId.get(r.order_id)
      r.order_status = o?.status ?? null
      r.order_inspection_at = o?.inspection_scheduled_at ?? null
    }
  }

  // Batch read-through: статус лота Carlink для воронок с торгами. Один
  // SELECT на страницу; лиды без лота получают null (бейдж «не создан»).
  const lotIds = items
    .filter((r) => leadPipelineUi(r.pipeline).lotBadgeStatuses.length > 0)
    .map((r) => r.id)
  if (lotIds.length > 0) {
    const { data: lots } = await supabase
      .from('carlink_lots')
      .select('lead_id, status')
      .in('lead_id', lotIds)
    const byLead = new Map<string, string | null>(
      (lots ?? []).map((l) => [(l as { lead_id: string }).lead_id, (l as { status: string | null }).status]),
    )
    for (const r of items) {
      if (lotIds.includes(r.id)) r.lot_status = byLead.get(r.id) ?? null
    }
  }

  // Batch read-through: имя оператора, взявшего лид. Для колонки «Взял»
  // в таблице. Один SELECT для всех уникальных assigned_to страницы.
  const assignedIds = Array.from(
    new Set(items.map((r) => r.assigned_to).filter((v): v is string => !!v)),
  )
  if (assignedIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', assignedIds)
    const byId = new Map<string, string>(
      (profiles ?? []).map((p) => [
        (p as { id: string }).id,
        (p as { full_name: string }).full_name,
      ]),
    )
    for (const r of items) {
      if (r.assigned_to) r.assigned_to_name = byId.get(r.assigned_to) ?? null
    }
  }

  return { items, total: count ?? 0 }
}

/**
 * Точные счётчики стадий канбана по ВСЕЙ базе (а не по загруженной странице).
 * Канбан рендерит лишь окно новейших лидов, поэтому терминальные колонки
 * («Успешно»/«Отменён» — самые старые лиды) в бейджах недосчитывались. RPC
 * повторяет deriveLeadStage и поиск listInspectionLeads. Возвращает map
 * stage → count (отсутствующие стадии = 0). См. миграцию 20260626100000.
 */
export async function getLeadStageCounts(
  search?: string,
  pipeline: LeadPipeline = 'avito',
): Promise<Partial<Record<InspectionLeadStage, number>>> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('get_inspection_lead_stage_counts', {
    p_search: search && search.trim() ? search.trim() : null,
    p_pipeline: pipeline,
  })
  if (error) throw new Error(error.message)
  return (data ?? {}) as Partial<Record<InspectionLeadStage, number>>
}

// ── «Есть другие лиды» (пилюля в карточке лида) ─────────────────────────────
// Другие лиды с тем же телефоном клиента. Телефоны в БД нормализованы
// (+7XXXXXXXXXX) и проиндексированы (inspection_leads_client_phone_idx),
// поэтому точный eq-матч дешёвый при любом объёме таблицы.

export interface OtherLeadRow {
  id: string
  created_at: string
  vehicle_brand: string | null
  vehicle_model: string | null
  status: InspectionLeadStatus
  order_id: string | null
  /** Read-through: статус связанного заказа (если лид сконвертирован). */
  order_status?: string | null
}

export interface OtherLeadsResult {
  items: OtherLeadRow[]
  /** Полное число других лидов с этим номером (без учёта limit). */
  total: number
}

/**
 * Счётчик для пилюли: сколько ДРУГИХ лидов с этим client_phone.
 * head-запрос — строки не выгружаются, только count.
 */
export async function countOtherLeadsByClientPhone(params: {
  phone: string
  excludeLeadId: string
}): Promise<number> {
  const supabase = createClient()
  const { count, error } = await supabase
    .from('inspection_leads')
    .select('id', { count: 'exact', head: true })
    .eq('client_phone', params.phone)
    .neq('id', params.excludeLeadId)
  if (error) throw new Error(error.message)
  return count ?? 0
}

/**
 * Список других лидов с этим client_phone, новые сверху. Грузится только
 * при раскрытии пилюли; «Показать ещё» — повторный вызов с большим limit.
 */
export async function listOtherLeadsByClientPhone(params: {
  phone: string
  excludeLeadId: string
  limit?: number
}): Promise<OtherLeadsResult> {
  const supabase = createClient()
  const { phone, excludeLeadId, limit = 10 } = params
  const { data, count, error } = await supabase
    .from('inspection_leads')
    .select('id, created_at, vehicle_brand, vehicle_model, status, order_id', {
      count: 'exact',
    })
    .eq('client_phone', phone)
    .neq('id', excludeLeadId)
    .order('created_at', { ascending: false })
    .range(0, limit - 1)
  if (error) throw new Error(error.message)
  const items = (data ?? []) as OtherLeadRow[]

  // Batch read-through: статус заказа для сконвертированных лидов — один
  // SELECT на весь список (тот же приём, что в listInspectionLeads).
  const orderIds = items.map((r) => r.order_id).filter((v): v is string => !!v)
  if (orderIds.length > 0) {
    const { data: orders } = await supabase
      .from('orders')
      .select('id, status')
      .in('id', orderIds)
    const byId = new Map<string, string>(
      (orders ?? []).map((o) => [(o as { id: string }).id, (o as { status: string }).status]),
    )
    for (const r of items) {
      if (r.order_id) r.order_status = byId.get(r.order_id) ?? null
    }
  }

  return { items, total: count ?? items.length }
}

/**
 * Кластер «тот же телефон + то же объявление Avito (item_id)» — это и есть
 * дубли одной заявки (совпадает с логикой антидубля). Возвращаем ДРУГИЕ
 * такие лиды (кроме самого), старые сверху — чтобы первый в списке был
 * «оригиналом». Кластер маленький, грузим целиком (cap 50).
 */
export async function listSameAdLeads(params: {
  phone: string
  itemId: number
  excludeLeadId: string
}): Promise<OtherLeadRow[]> {
  const supabase = createClient()
  const { phone, itemId, excludeLeadId } = params
  const { data, error } = await supabase
    .from('inspection_leads')
    .select('id, created_at, vehicle_brand, vehicle_model, status, order_id')
    .eq('client_phone', phone)
    .eq('item_id', itemId)
    .neq('id', excludeLeadId)
    .order('created_at', { ascending: true })
    .range(0, 49)
  if (error) throw new Error(error.message)
  const items = (data ?? []) as OtherLeadRow[]

  const orderIds = items.map((r) => r.order_id).filter((v): v is string => !!v)
  if (orderIds.length > 0) {
    const { data: orders } = await supabase
      .from('orders')
      .select('id, status')
      .in('id', orderIds)
    const byId = new Map<string, string>(
      (orders ?? []).map((o) => [(o as { id: string }).id, (o as { status: string }).status]),
    )
    for (const r of items) {
      if (r.order_id) r.order_status = byId.get(r.order_id) ?? null
    }
  }
  return items
}

export async function getInspectionLead(id: string): Promise<InspectionLeadRow> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('inspection_leads')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw new Error(error.message)
  const lead = data as InspectionLead

  // Read-through: статус и URL отчёта связанного заказа (если лид сконвертирован).
  // order_report_url нужен карточке, чтобы включать кнопку «Отправить SMS с отчётом».
  let order_status: string | null = null
  let order_report_url: string | null = null
  let order_inspection_at: string | null = null
  if (lead.order_id) {
    const { data: o } = await supabase
      .from('orders')
      .select('status, external_inspection_report_url, inspection_scheduled_at')
      .eq('id', lead.order_id)
      .maybeSingle()
    order_status = (o as { status: string } | null)?.status ?? null
    order_report_url =
      (o as { external_inspection_report_url: string | null } | null)
        ?.external_inspection_report_url ?? null
    order_inspection_at =
      (o as { inspection_scheduled_at: string | null } | null)?.inspection_scheduled_at ?? null
  }

  // Read-through: имя оператора, взявшего лид. Используется в header'е
  // карточки для pill «👤 у Алексея / у вас». auth.uid() в UI сравнивается
  // отдельно с assigned_to.
  let assigned_to_name: string | null = null
  if (lead.assigned_to) {
    const { data: p } = await supabase
      .from('profiles')
      .select('full_name')
      .eq('id', lead.assigned_to)
      .maybeSingle()
    assigned_to_name = (p as { full_name: string } | null)?.full_name ?? null
  }

  return { ...lead, order_status, order_report_url, order_inspection_at, assigned_to_name }
}

// ---------------------------------------------------------------------------
// RPC обёртки для палитры действий в карточке лида.
//
// claim_lead / release_lead / send_form_sms_and_advance — миграция
// 20260601100000_lead_claim_and_awaiting_sms.sql.
// ---------------------------------------------------------------------------

/** Взять лид в работу (claim ownership). Атомарно: new → in_progress,
 *  assigned_to=auth.uid(). Бросает если лид уже у другого / в терминале. */
export async function claimLead(leadId: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.rpc('claim_lead', { p_lead_id: leadId })
  if (error) throw new Error(error.message)
}

/** Результат звонка ФО (RPC set_lead_call_result). «Передано в ОО» переводит
 *  лид в ОО, отказ и стоп-фактор отменяют, «перезвонить» ставит напоминание.
 *  Не хватает полей для передачи — ошибка `missing_fields:a,b`. */
export async function setLeadCallResult(
  leadId: string,
  result: CallResult,
  opts: { callId?: string | null; note?: string | null; callbackAt?: string | null } = {},
): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.rpc('set_lead_call_result', {
    p_lead_id: leadId,
    p_result: result,
    p_call_id: opts.callId ?? null,
    p_note: opts.note ?? null,
    p_callback_at: opts.callbackAt ?? null,
  })
  if (error) throw new Error(error.message)
}

export interface LeadCallResultRow {
  call_id: string | null
  result: CallResult
  attempt: number | null
  created_at: string
}

/** История результатов звонка лида (новые сверху): метки у звонков. */
export async function listLeadCallResults(leadId: string): Promise<LeadCallResultRow[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('lead_call_results')
    .select('call_id, result, attempt, created_at')
    .eq('lead_id', leadId)
    .order('created_at', { ascending: false })
  if (error) throw new Error(error.message)
  return (data ?? []) as LeadCallResultRow[]
}

/** Отказаться от лида. Атомарно: in_progress → new, assigned_to=NULL.
 *  Требует assigned_to=auth.uid() И status='in_progress'. */
export async function releaseLead(leadId: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.rpc('release_lead', { p_lead_id: leadId })
  if (error) throw new Error(error.message)
}

/** Кнопка «Отправить SMS клиенту со ссылкой на форму». Атомарно: auto-claim
 *  если свободен + перевод в awaiting_payment. SMS отправит триггер
 *  inspection_leads_awaiting_payment_sms_au (на первой передаче). Повторный
 *  вызов на awaiting_payment-лиде — noop по статусу; для повторной отправки
 *  SMS используется отдельная кнопка SendSmsToLeadButton с edge send-sms. */
export async function sendFormSmsAndAdvance(leadId: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.rpc('send_form_sms_and_advance', { p_lead_id: leadId })
  if (error) throw new Error(error.message)
}

/**
 * Частичное обновление лида. Источник изменения для триггера журнала —
 * 'dispatcher' (определяется в БД: current_setting не задан + auth.uid() есть).
 */
export async function updateInspectionLead(
  id: string,
  patch: Partial<{
    status: InspectionLeadStatus
    client_name: string | null
    client_phone: string | null
    location: string | null
    vehicle_brand: string | null
    vehicle_model: string | null
    vehicle_year: number | null
    vehicle_vin: string | null
    vehicle_license_plate: string | null
    seller_name: string | null
    seller_phone: string | null
    item_id: number | null
    item_link: string | null
    /** B2C: котируемая цена осмотра, ₽. Редактируется только до оплаты. */
    price_amount: number
    // payment_amount / payment_at / tbank_payment_id / refunded_at — immutable
    // на стороне БД (триггер inspection_leads_immutable_payment).
    // Они заполняются edge функциями (webhook от Т-Банка и tbank-refund-payment)
    // и не редактируются админкой.
    /** Структурная причина отмены (ставится вместе со status='cancelled'). */
    cancel_reason: CancelReasonCode | null
    /** Комментарий к отмене (свободный текст, в дополнение к cancel_reason). */
    cancel_comment: string | null
    /** Комментарий для партнёра (СберАвто видит в кабинете). */
    partner_comment: string | null
    /** СберАвто: адрес осмотра, квалификация ФО. */
    inspection_address: string | null
    qualification_note: string | null
    /** СберАвто: ФО связался с клиентом — событие «Связались» в канал. */
    contacted_at: string | null
    /** Тестовый лид (миграция stryzh 20261104180000). */
    is_test: boolean
  }>,
): Promise<void> {
  if (Object.keys(patch).length === 0) return
  const supabase = createClient()
  const { error } = await supabase
    .from('inspection_leads')
    .update(patch)
    .eq('id', id)
  if (error) throw new Error(error.message)
}

/**
 * Запуск обогащения из Автотеки. Edge enrich-lead-autoteka заказывает превью
 * и отчёт и отвечает `pending`; состояние — в лиде (autoteka_state), cron на
 * сервере дожимает отчёт раз в минуту, карточка обновляется по Realtime.
 */
export interface EnrichResult {
  status: 'ok' | 'error' | 'pending'
  state?: string
  fields?: {
    brand: string | null
    model: string | null
    year: number | null
    vin: string | null
    regNumber: string | null
  }
  reason?: string
  detail?: string
}

export async function runAutotekaEnrichment(leadId: string): Promise<EnrichResult> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('enrich-lead-autoteka', {
    body: { leadId },
  })
  if (error) {
    // supabase-js упаковывает HTTP ошибки сюда; пытаемся достать message.
    return { status: 'error', reason: 'invoke_failed', detail: error.message }
  }
  return (data ?? { status: 'error', reason: 'no_response' }) as EnrichResult
}

// ── Звонки Mango в карточке лида ────────────────────────────────────────────
// Ищем по нормализованному телефону клиента (11 цифр, 8→7) — так же хранится
// mango_calls.client_phone. Индекс mango_calls_client_phone_idx в базе есть.

/** Нормализация телефона к 11-значному виду (как в mango_calls.client_phone). */
export function normalizePhoneDigits(raw: string | null): string | null {
  if (!raw) return null
  let d = raw.replace(/\D/g, '')
  if (d.length === 11 && d.startsWith('8')) d = '7' + d.slice(1)
  if (d.length === 10) d = '7' + d
  return d.length === 11 ? d : null
}

/** Окно звонков лида: [from, to). from = created_at лида, to = created_at
 *  следующего лида того же телефона (или null = «до сейчас»). */
export interface CallsWindow {
  from?: string | null
  to?: string | null
}

export async function countCallsByClientPhone(
  phone: string,
  win: CallsWindow = {},
): Promise<number> {
  const supabase = createClient()
  let q = supabase
    .from('mango_calls')
    .select('id', { count: 'exact', head: true })
    .eq('client_phone', phone)
  if (win.from) q = q.gte('started_at', win.from)
  if (win.to) q = q.lt('started_at', win.to)
  const { count, error } = await q
  if (error) throw new Error(error.message)
  return count ?? 0
}

export async function listCallsByClientPhone(params: {
  phone: string
  limit?: number
  win?: CallsWindow
}): Promise<MangoCall[]> {
  const supabase = createClient()
  const { phone, limit = 5, win = {} } = params
  let q = supabase
    .from('mango_calls')
    .select('id, direction, client_phone, manager_name, started_at, duration_sec, recording_ids, summary')
    .eq('client_phone', phone)
  if (win.from) q = q.gte('started_at', win.from)
  if (win.to) q = q.lt('started_at', win.to)
  const { data, error } = await q
    .order('started_at', { ascending: false })
    .range(0, limit - 1)
  if (error) throw new Error(error.message)
  return (data ?? []) as MangoCall[]
}

/** Звонок, ждущий результата ФО (inspection_leads.pending_call_id), с данными
 *  из mango_calls (в т.ч. сводка). null: такого звонка нет. */
export async function getLeadPendingCall(leadId: string): Promise<MangoCall | null> {
  const supabase = createClient()
  const { data: lead, error } = await supabase
    .from('inspection_leads')
    .select('pending_call_id')
    .eq('id', leadId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  const callId = (lead as { pending_call_id?: string | null } | null)?.pending_call_id
  if (!callId) return null
  const { data: call, error: callErr } = await supabase
    .from('mango_calls')
    .select('id, direction, client_phone, manager_name, started_at, duration_sec, recording_ids, summary')
    .eq('id', callId)
    .maybeSingle()
  if (callErr) throw new Error(callErr.message)
  return (call as MangoCall | null) ?? null
}

/** created_at ближайшего следующего лида того же клиента (по raw-телефону) —
 *  верхняя граница окна звонков. null, если этот лид — самый свежий. */
export async function getNextLeadCreatedAt(params: {
  clientPhone: string
  afterCreatedAt: string
}): Promise<string | null> {
  const supabase = createClient()
  const { clientPhone, afterCreatedAt } = params
  const { data, error } = await supabase
    .from('inspection_leads')
    .select('created_at')
    .eq('client_phone', clientPhone)
    .gt('created_at', afterCreatedAt)
    .order('created_at', { ascending: true })
    .limit(1)
  if (error) throw new Error(error.message)
  return (data?.[0]?.created_at as string | undefined) ?? null
}

// ── SMS в карточке лида (раздел «Коммуникации») ────────────────────────────
// Лог пишется edge send-sms в public.sms_outbox (миграция 20260619180000).
// Окно — то же, что и у звонков (от created_at этого лида до created_at
// следующего лида того же клиента, см. getNextLeadCreatedAt).

/** SMS по телефону клиента либо по лиду (SMS дилерам о торгах идут на чужие
 *  телефоны, их держит только lead_id). */
function smsFilter(phone: string | null, leadId: string | null): string {
  const parts: string[] = []
  if (phone) parts.push(`phone.eq.${phone}`)
  if (leadId) parts.push(`lead_id.eq.${leadId}`)
  return parts.join(',')
}

export async function countSmsByClientPhone(
  phone: string | null,
  win: CallsWindow = {},
  leadId: string | null = null,
): Promise<number> {
  const supabase = createClient()
  let q = supabase
    .from('sms_outbox')
    .select('id', { count: 'exact', head: true })
    .or(smsFilter(phone, leadId))
  if (win.from) q = q.gte('sent_at', win.from)
  if (win.to) q = q.lt('sent_at', win.to)
  const { count, error } = await q
  if (error) throw new Error(error.message)
  return count ?? 0
}

export async function listSmsByClientPhone(params: {
  phone: string | null
  leadId?: string | null
  limit?: number
  win?: CallsWindow
}): Promise<LeadSms[]> {
  const supabase = createClient()
  const { phone, leadId = null, limit = 5, win = {} } = params
  let q = supabase
    .from('sms_outbox')
    .select('id, phone, message, status, source, sent_at, lead_id, actor_id')
    .or(smsFilter(phone, leadId))
  if (win.from) q = q.gte('sent_at', win.from)
  if (win.to) q = q.lt('sent_at', win.to)
  const { data, error } = await q
    .order('sent_at', { ascending: false })
    .range(0, limit - 1)
  if (error) throw new Error(error.message)
  return resolveActorNames((data ?? []) as LeadSms[], supabase) as Promise<LeadSms[]>
}

// ── Клиентские события на лиде (раздел «Коммуникации») ─────────────────────
// public.lead_events заполняется триггерами БД (form_submitted /
// payment_started / payment_completed) и edge `landing-track-event`
// (form_opened / status_page_viewed beacon с лендинга). Связь жёсткая по
// lead_id, окно лида не нужно — лид сам отрезает свои события.

export async function countLeadEventsByLead(leadId: string): Promise<number> {
  const supabase = createClient()
  const { count, error } = await supabase
    .from('lead_events')
    .select('id', { count: 'exact', head: true })
    .eq('lead_id', leadId)
    // События Автотеки (autoteka_*) живут в разделе «Активность», а не здесь —
    // «Коммуникации» остаётся клиентской лентой (форма/оплата/просмотр).
    .not('type', 'like', 'autoteka%')
  if (error) throw new Error(error.message)
  return count ?? 0
}

export async function listLeadEventsByLead(params: {
  leadId: string
  limit?: number
}): Promise<LeadEvent[]> {
  const supabase = createClient()
  const { leadId, limit = 5 } = params
  const { data, error } = await supabase
    .from('lead_events')
    .select('id, lead_id, type, at, actor_id')
    .eq('lead_id', leadId)
    // см. countLeadEventsByLead: события Автотеки — в «Активности», не тут.
    .not('type', 'like', 'autoteka%')
    .order('at', { ascending: false })
    .range(0, limit - 1)
  if (error) throw new Error(error.message)
  return resolveActorNames((data ?? []) as LeadEvent[], supabase) as Promise<LeadEvent[]>
}

/** Подтягивает `actor_name` из `profiles.full_name` одним запросом для
 *  массива записей с `actor_id`. Записи без `actor_id` пропускаются. */
async function resolveActorNames<T extends { actor_id?: string | null; actor_name?: string | null }>(
  rows: T[],
  supabase: ReturnType<typeof createClient>,
): Promise<T[]> {
  const ids = Array.from(
    new Set(rows.map((r) => r.actor_id).filter((v): v is string => !!v)),
  )
  if (ids.length === 0) return rows
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, full_name')
    .in('id', ids)
  const byId = new Map<string, string>(
    (profiles ?? []).map((p) => [
      (p as { id: string }).id,
      (p as { full_name: string }).full_name,
    ]),
  )
  return rows.map((r) =>
    r.actor_id ? { ...r, actor_name: byId.get(r.actor_id) ?? null } : r,
  )
}

/** Деталь звонка: ленивая транскрипция + свежая ссылка на запись. */
export async function getMangoCallDetail(callId: string): Promise<MangoCallDetail> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('mango-call-detail', {
    body: { callId },
  })
  if (error) throw new Error(error.message)
  return (data ?? { status: 'error', transcript: [], recordingUrl: null }) as MangoCallDetail
}

/**
 * История изменений лида (новые сверху). Заодно подтягиваем имена авторов
 * — diff показывается компактно, имя нужно как часть подписи источника.
 */
export async function listInspectionLeadChanges(
  leadId: string,
): Promise<InspectionLeadChangeRow[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('inspection_lead_changes')
    .select('*')
    .eq('lead_id', leadId)
    .order('changed_at', { ascending: false })
    .limit(500)
  if (error) throw new Error(error.message)
  const changes = (data ?? []) as InspectionLeadChange[]

  const userIds = Array.from(
    new Set(changes.map((c) => c.changed_by).filter((v): v is string => !!v)),
  )
  const nameById = new Map<string, string>()
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds)
    for (const p of (profiles ?? []) as Array<{ id: string; full_name: string | null }>) {
      nameById.set(p.id, p.full_name ?? '')
    }
  }
  return changes.map((c) => ({
    ...c,
    changed_by_name: c.changed_by ? (nameById.get(c.changed_by) || null) : null,
  }))
}

/**
 * Лента комментариев диспетчера (append-only, новые сверху). Подтягиваем
 * имена авторов одним запросом (как в истории изменений).
 */
export async function listLeadComments(
  leadId: string,
): Promise<InspectionLeadComment[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('inspection_lead_comments')
    .select('*')
    .eq('lead_id', leadId)
    .order('created_at', { ascending: false })
    .limit(500)
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as Array<Omit<InspectionLeadComment, 'author_name'>>

  const userIds = Array.from(
    new Set(rows.map((r) => r.author_id).filter((v): v is string => !!v)),
  )
  const nameById = new Map<string, string>()
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds)
    for (const p of (profiles ?? []) as Array<{ id: string; full_name: string | null }>) {
      nameById.set(p.id, p.full_name ?? '')
    }
  }
  return rows.map((r) => ({
    ...r,
    author_name: r.author_id ? (nameById.get(r.author_id) || null) : null,
  }))
}

/**
 * Добавить комментарий. author_id проставляет БД из auth.uid() (триггер),
 * поэтому из клиента передаём только текст. Пустой/пробельный — не пишем.
 */
export async function addLeadComment(leadId: string, body: string): Promise<void> {
  const trimmed = body.trim()
  if (trimmed === '') return
  const supabase = createClient()
  const { error } = await supabase
    .from('inspection_lead_comments')
    .insert({ lead_id: leadId, body: trimmed })
  if (error) throw new Error(error.message)
}

/**
 * Команда напоминания `@avs …` из комментария → edge `lead-reminder`.
 * Парсит через ai-proxy (Sonnet), создаёт/отменяет напоминание и пишет
 * системный коммент-эхо в ту же ленту. commandText — текст без префикса `@avs`.
 */
export async function requestLeadReminder(leadId: string, commandText: string): Promise<void> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('lead-reminder', {
    body: { lead_id: leadId, command_text: commandText },
  })
  if (error) throw new Error(error.message)
  const res = data as { ok?: boolean; error?: string }
  if (res?.error) throw new Error(res.error)
}

/**
 * Отправить тестовый payload в /avito-lead через прокси-функцию
 * `avito-lead-test`. Прокси читает реальный X-Webhook-Token из env на
 * сервере, поэтому токен в код фронта не зашит. Тестовый лид проходит весь
 * боевой путь: проверка токена → валидация → дедуп → RPC создания.
 *
 * Возвращает то же, что /avito-lead: `{ status:'ok', leadId, duplicate?:true }`
 * либо `{ status:'error', reason, ... }`.
 */
export interface TestAvitoLeadPayload {
  name: string
  phone: string
  region: string
  itemId?: number | null
  itemLink?: string | null
  /** Маркер источника — попадёт в inspection_leads.source. Дефолт на бэке — "AvitoTest". */
  clientId?: string
}

export type TestAvitoLeadResult =
  | { status: 'ok'; leadId: string; duplicate?: boolean }
  | { status: 'error'; reason: string; detail?: string; missing?: Record<string, boolean> }

export async function submitTestAvitoLead(
  payload: TestAvitoLeadPayload,
): Promise<TestAvitoLeadResult> {
  const supabase = createClient()
  const body: Record<string, unknown> = {
    name: payload.name,
    phone: payload.phone,
    region: payload.region,
  }
  if (payload.clientId && payload.clientId.trim() !== '') {
    body.clientId = payload.clientId.trim()
  }
  if (payload.itemId !== null && payload.itemId !== undefined) {
    body.itemId = payload.itemId
  }
  if (payload.itemLink !== null && payload.itemLink !== undefined && payload.itemLink !== '') {
    body.itemLink = payload.itemLink
  }

  // functions.invoke не делает форс-рефреш токена перед вызовом: если вкладка
  // провисела дольше времени жизни access-token (~1ч), полетит протухший токен
  // и avito-lead-test ответит 401 invalid_session. getSession() сам обновит
  // токен по refresh-токену; явно прокидываем его заголовком, чтобы invoke
  // взял именно свежий. Нет сессии вовсе → понятная ошибка вместо invalid_session.
  const { data: sessionData } = await supabase.auth.getSession()
  const accessToken = sessionData.session?.access_token
  if (!accessToken) {
    return { status: 'error', reason: 'session_expired' }
  }

  const { data, error } = await supabase.functions.invoke('avito-lead-test', {
    body,
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (error) {
    // supabase-js при HTTP != 2xx кладёт тело ответа в error.context.
    // Пытаемся вытащить структурированный reason из тела.
    const ctx = (error as { context?: Response }).context
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as TestAvitoLeadResult
        return parsed
      } catch {
        // fallthrough
      }
    }
    return { status: 'error', reason: 'invoke_failed', detail: error.message }
  }
  return (data ?? { status: 'error', reason: 'no_response' }) as TestAvitoLeadResult
}

/** Счётчик новых лидов — для бабла в сайдбаре. */
export async function getNewLeadsCount(): Promise<number> {
  const supabase = createClient()
  const { count, error } = await supabase
    .from('inspection_leads')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'new')
  if (error) throw new Error(error.message)
  return count ?? 0
}

/**
 * Ручная конвертация лида в заказ на осмотр (кнопка «Создать заказ»),
 * в т.ч. без оплаты (force-create). Edge `create-order-from-lead` проверяет
 * роль admin/dispatcher и зовёт RPC create_order_from_lead под service_role.
 */
export async function createOrderFromLead(
  leadId: string,
): Promise<{ ok: boolean; orderId?: string; reason?: string; detail?: string }> {
  const supabase = createClient()
  const { data, error } = await supabase.functions.invoke('create-order-from-lead', {
    body: { leadId },
  })
  if (error) {
    const ctx = (error as { context?: Response }).context
    if (ctx && typeof ctx.json === 'function') {
      try {
        return (await ctx.json()) as { ok: boolean; reason?: string; detail?: string }
      } catch {
        // fallthrough
      }
    }
    return { ok: false, reason: 'invoke_failed', detail: error.message }
  }
  return (data ?? { ok: false, reason: 'no_response' }) as { ok: boolean; orderId?: string }
}

/**
 * Ручное создание лида диспетчером (физик не с Avito/лендинга). Edge
 * `manual-lead` создаёт лид с source='Manual' и проставляет цену.
 */
export async function createManualLead(payload: {
  name: string
  phone: string
  location: string
  price?: number
}): Promise<{ status: string; leadId?: string; duplicate?: boolean; reason?: string }> {
  const supabase = createClient()
  const body: Record<string, unknown> = {
    name: payload.name,
    phone: payload.phone,
    location: payload.location,
  }
  if (payload.price != null) body.price = payload.price
  const { data, error } = await supabase.functions.invoke('manual-lead', { body })
  if (error) {
    const ctx = (error as { context?: Response }).context
    if (ctx && typeof ctx.json === 'function') {
      try {
        return (await ctx.json()) as { status: string; reason?: string }
      } catch {
        // fallthrough
      }
    }
    return { status: 'error', reason: 'invoke_failed' }
  }
  return (data ?? { status: 'error', reason: 'no_response' }) as {
    status: string
    leadId?: string
    duplicate?: boolean
  }
}

/** Поля лида СберАвто из анкеты (см. utils/sberautoParse). */
export interface SberAutoLeadPayload {
  external_id: string | null
  client_name: string | null
  client_phone: string | null
  location: string | null
  vehicle_brand: string | null
  vehicle_model: string | null
  vehicle_vin: string | null
  vehicle_license_plate: string | null
  trade_in_target_vehicle: string | null
  inspection_address: string | null
  qualification_note: string | null
  /** Сообщение из мессенджера дословно — сохраняется в partner_messages. */
  raw_message: string | null
}

/**
 * Создать лид СберАвто или дополнить существующий с тем же номером CRM.
 * RPC upsert_sberauto_lead (миграция stryzh 20261030120000), только
 * admin/dispatcher.
 */
export async function upsertSberAutoLead(
  payload: SberAutoLeadPayload,
): Promise<{ leadId: string; created: boolean }> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('upsert_sberauto_lead', { p_data: payload })
  if (error) throw new Error(error.message)
  const r = data as { lead_id: string; created: boolean }
  return { leadId: r.lead_id, created: r.created }
}

/** Лид СберАвто с таким номером CRM, если уже есть — для предупреждения
 *  «дополним существующий» в диалоге. */
export async function findSberAutoLeadByExternalId(
  externalId: string,
): Promise<Pick<InspectionLead, 'id' | 'client_name' | 'status'> | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('inspection_leads')
    .select('id, client_name, status')
    .eq('pipeline', 'sberauto')
    .eq('partner_external_id', externalId)
    .maybeSingle()
  if (error) throw new Error(error.message)
  return (data as Pick<InspectionLead, 'id' | 'client_name' | 'status'> | null) ?? null
}

/** Оценка CM Expert: PDF в bucket cm-expert-reports, путь в лиде; новая замещает старую
 *  (старый файл остаётся, смену пути пишет журнал лида). */
export async function uploadCmExpertPdf(leadId: string, file: File): Promise<string> {
  const supabase = createClient()
  const path = `${leadId}/${Date.now()}-cm-expert.pdf`
  const { error: upErr } = await supabase.storage
    .from('cm-expert-reports')
    .upload(path, file, { contentType: 'application/pdf', upsert: false })
  if (upErr) throw new Error(upErr.message)
  const { error } = await supabase.from('inspection_leads').update({ cm_expert_pdf_path: path }).eq('id', leadId)
  if (error) throw new Error(error.message)
  return path
}

/** Удалить оценку CM Expert: файл из bucket и путь из лида (дату снимает триггер). */
export async function deleteCmExpertPdf(leadId: string, path: string): Promise<void> {
  const supabase = createClient()
  const { error: rmErr } = await supabase.storage.from('cm-expert-reports').remove([path])
  if (rmErr) throw new Error(rmErr.message)
  const { error } = await supabase.from('inspection_leads').update({ cm_expert_pdf_path: null }).eq('id', leadId)
  if (error) throw new Error(error.message)
}

export async function cmExpertPdfUrl(path: string, leadNo: number): Promise<string> {
  const { data, error } = await createClient().storage
    .from('cm-expert-reports')
    .createSignedUrl(path, 600, { download: `cm-expert-${leadNo}.pdf` })
  if (error || !data?.signedUrl) throw new Error(error?.message ?? 'no_url')
  return data.signedUrl
}

export async function downloadAutotekaPdf(leadId: string, storedPath?: string | null): Promise<void> {
  const supabase = createClient()
  // PDF уже выгружен в фоне после готовности отчёта — отдаём из Storage сразу.
  if (storedPath) {
    const name = `autoteka-${storedPath.split('/').pop() ?? 'report.pdf'}`
    const { data, error } = await supabase.storage
      .from('autoteka-reports')
      .createSignedUrl(storedPath, 600, { download: name })
    if (!error && data?.signedUrl) {
      window.location.assign(data.signedUrl)
      return
    }
  }
  const { data: sessionData } = await supabase.auth.getSession()
  const accessToken = sessionData.session?.access_token
  if (!accessToken) throw new Error('session_expired')
  const res = await fetch(`${SUPABASE_URL}/functions/v1/autoteka-report-pdf`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ leadId }),
  })
  if (!res.ok) {
    let reason = String(res.status)
    try {
      const j = (await res.json()) as { reason?: string }
      if (j.reason) reason = j.reason
    } catch {
      /* keep status */
    }
    throw new Error(reason)
  }
  const name = res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ?? 'autoteka.pdf'
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
